import { useEffect, useRef, useState } from 'react';
import type { ParkingSpot, VehicleType, ParkingType, AccessType } from '../types';
import { VEHICLE_LABELS, PARKING_LABELS, SECURITY_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import { useTipSeen } from '../hooks/useTour';

interface SpotFormProps {
  id: string;
  lat: number;
  lng: number;
  onSubmit: (data: Omit<ParkingSpot, 'id' | 'created_at' | 'interest_count' | 'comment_count'> & { email: string; phone: string }) => Promise<void>;
}

export default function SpotForm({ id, lat, lng, onSubmit }: SpotFormProps) {
  const [formData, setFormData] = useState({
    lat,
    lng,
    parking_type: 'apartment' as ParkingType,
    vehicle_types: [] as VehicleType[],
    capacity: 1,
    is_free: false,
    price_hourly: '',
    price_daily: '',
    price_monthly: '',
    is_covered: false,
    security_features: [] as string[],
    access_type: '24x7' as AccessType,
    access_hours: { from: '06:00', to: '22:00' },
    ev_charging: false,
    dimensions: '',
    building_name: '',
    description: '',
    email: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const tip = useTipSeen('form_spot');

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const toggleVehicle = (vehicle: VehicleType) => {
    setFormData((prev) => ({
      ...prev,
      vehicle_types: prev.vehicle_types.includes(vehicle)
        ? prev.vehicle_types.filter((v) => v !== vehicle)
        : [...prev.vehicle_types, vehicle],
    }));
  };

  const toggleSecurity = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      security_features: prev.security_features.includes(feature)
        ? prev.security_features.filter((f) => f !== feature)
        : [...prev.security_features, feature],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.vehicle_types.length === 0) {
      trackEvent('add_pin_validation_error', { pin_type: 'spot', error_field: 'vehicle_types' });
      setError('Please select at least one vehicle type');
      return;
    }

    if (!formData.is_free) {
      const prices = [formData.price_hourly, formData.price_daily, formData.price_monthly];
      if (prices.some((price) => !price)) {
        trackEvent('add_pin_validation_error', { pin_type: 'spot', error_field: 'prices' });
        setError('Please enter hourly, daily and monthly prices for paid parking');
        return;
      }
    }

    try {
      await onSubmit({
        ...formData,
        price_hourly: formData.is_free ? 0 : formData.price_hourly ? parseFloat(formData.price_hourly) : null,
        price_daily: formData.is_free ? 0 : formData.price_daily ? parseFloat(formData.price_daily) : null,
        price_monthly: formData.is_free ? 0 : formData.price_monthly ? parseFloat(formData.price_monthly) : null,
        dimensions: formData.dimensions || null,
        building_name: formData.building_name || null,
        description: formData.description || null,
        access_hours: formData.access_type === 'time_restricted' ? formData.access_hours : null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    }
  };

  return (
    <form id={id} onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 text-blue-800 text-xs p-2 rounded-lg">
        Pin location: {lat.toFixed(4)}, {lng.toFixed(4)}
      </div>

      {!tip.seen && (
        <div className="bg-parksy-50 border border-parksy-200 text-parksy-900 text-xs p-3 rounded-lg flex items-start gap-2" data-testid="form-tip-spot">
          <span className="text-base leading-none">💡</span>
          <div className="flex-1">
            <div className="font-semibold mb-0.5">Listing a spot</div>
            <p className="leading-relaxed text-parksy-800">
              For paid parking, all three prices (hourly, daily, monthly) are required so seekers can filter on any duration. Free spots only need the toggle.
            </p>
          </div>
          <button
            type="button"
            onClick={tip.mark}
            className="text-parksy-700 hover:text-parksy-900 font-semibold px-1"
            aria-label="Dismiss tip"
          >
            &times;
          </button>
        </div>
      )}

      {error && (
        <div ref={errorRef} className="bg-red-50 text-red-700 text-sm p-2 rounded-lg">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Parking Type</label>
        <select
          value={formData.parking_type}
          onChange={(e) => setFormData({ ...formData, parking_type: e.target.value as any })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {Object.entries(PARKING_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Vehicle Types</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(VEHICLE_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleVehicle(key as VehicleType)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                formData.vehicle_types.includes(key as VehicleType)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_free}
          onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
          className="rounded border-gray-300 text-parksy-600"
        />
        <span className="text-sm font-medium text-gray-700">Free parking</span>
      </label>

      <div className={`grid grid-cols-3 gap-2 ${formData.is_free ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Hourly (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_hourly}
            onChange={(e) => setFormData({ ...formData, price_hourly: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Daily (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_daily}
            onChange={(e) => setFormData({ ...formData, price_daily: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Monthly (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_monthly}
            onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Capacity</label>
          <input
            type="number"
            min={1}
            value={formData.capacity}
            onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Access</label>
          <select
            value={formData.access_type}
            onChange={(e) => setFormData({ ...formData, access_type: e.target.value as any })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="24x7">24/7</option>
            <option value="day_only">Day only</option>
            <option value="time_restricted">Time restricted</option>
          </select>
        </div>
      </div>

      {formData.access_type === 'time_restricted' && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="time"
            value={formData.access_hours.from}
            onChange={(e) => setFormData({ ...formData, access_hours: { ...formData.access_hours, from: e.target.value } })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="time"
            value={formData.access_hours.to}
            onChange={(e) => setFormData({ ...formData, access_hours: { ...formData.access_hours, to: e.target.value } })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Security</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SECURITY_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleSecurity(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                formData.security_features.includes(key)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_covered}
            onChange={(e) => setFormData({ ...formData, is_covered: e.target.checked })}
            className="rounded border-gray-300 text-parksy-600"
          />
          <span className="text-sm text-gray-700">Covered parking</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.ev_charging}
            onChange={(e) => setFormData({ ...formData, ev_charging: e.target.checked })}
            className="rounded border-gray-300 text-parksy-600"
          />
          <span className="text-sm text-gray-700">EV charging available</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Building / Society Name</label>
        <input
          type="text"
          value={formData.building_name}
          onChange={(e) => setFormData({ ...formData, building_name: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="e.g., Sunshine Apartments"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Any other details..."
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="10-digit number"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Email & phone stay private. Shared only with matched seekers via email.
      </p>
    </form>
  );
}
